import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { urlConfig } from '../../config'

export interface BankDepositAccount {
    _id: string
    user: string
    first_name: string
    surname: string
    phone_no: string
    email: string
    bvn: string
    account_name: string
    account_no: string
    bank_name: string
    auto_settlement: string
    reference: string
    provider: string
    identity_type?: string
    date: string
    raw: Record<string, unknown>
    createdAt: string
}

export interface BVNVerificationRequest {
    bvn: string
}

export interface BVNVerificationResponse {
    verified: true
    source: 'kyc' | 'bvn'
    customerName: string
    _id?: string
    user?: string
    bvn?: string
    bvnVerifiedAt?: string
}

export interface PhoneVerificationRequest {
    phone_no: string
}

export interface PhoneVerificationResponse {
    _id?: string
    user?: string
    bvn?: string
    bvnVerifiedAt?: string
    phone_no: string
    phoneVerifiedAt: string
    customerName: string
}

export type CreateSubAccountResponse = BankDepositAccount

export interface DepositHistoryItem {
    _id: string
    user: string
    subAccount: {
        _id: string
        user: string
        first_name: string
        surname: string
        phone_no: string
        email: string
        bvn: string
        account_name: string
        account_no: string
        bank_name: string
        auto_settlement: string
        reference: string
        provider: string
        date: string
        raw: Record<string, unknown>
        createdAt: string
    }
    amount: number
    settlement: number
    charge: number
    reference: string
    first_name: string
    surname: string
    phone_no: string
    email: string
    bvn: string
    provider: string
    date: string
    raw: Record<string, unknown>
    status: string
    createdAt: string
    creditedAmount?: number
    fee?: number
    creditedAt?: string
}

export interface DepositHistoryResponse {
    data: DepositHistoryItem[]
    total: number
    page: number
    last_page: number
    per_page: number
}

export interface DepositHistoryParams {
    page: number
    limit: number
}

// Where the user stands on the BVN a virtual account needs. Absent on an
// older backend — fall back to the legacy flags then.
export type BvnState = 'not_required' | 'required' | 'pending_review' | 'rejected' | 'verified'

export interface StepsStatusResponse {
    bvn?: BvnState
    bvnRejectReason?: string | null
    kycIdentity: boolean
    bvnCaptured: boolean
    phoneCaptured: boolean
    hasAccount: boolean
}

export interface BvnAddonResponse {
    status: 'approved' | 'pending' | 'rejected'
    message: string | null
    kycId: string
}

export const bankDepositApi = createApi({
    reducerPath: 'bankDepositApi',
    baseQuery: fetchBaseQuery({
        baseUrl: urlConfig.bankDepositUrl,
        prepareHeaders: (headers) => {
            const token = localStorage.getItem('token')
            if (token) {
                headers.set('authorization', `Bearer ${token}`)
            }
            headers.set('Content-Type', 'application/json')
            return headers
        },
    }),
    tagTypes: ['BankDepositAccount'],
    endpoints: (builder) => ({
        // Is the bank-deposit feature switched on? (super-admin wallet setting
        // `bankDepositsEnabled`). Read before anything else — every other
        // endpoint here answers 503 while the feature is off.
        getBankDepositAvailability: builder.query<{ enabled: boolean }, void>({
            query: () => 'bank-deposits/availability',
        }),
        getBankDepositAccount: builder.query<BankDepositAccount, void>({
            query: () => 'bank-deposits/sub-account',
            providesTags: ['BankDepositAccount'],
        }),
        getBankDepositStepsStatus: builder.query<StepsStatusResponse, void>({
            query: () => 'bank-deposits/steps/status',
            providesTags: ['BankDepositAccount'],
        }),
        // Real BVN verification lives in the KYC service (YouVerify): the BVN
        // must belong to the same person as the user's verified KYC. Absolute
        // URL on purpose — this api's baseUrl is the wallets service — and it
        // sits here (not in kycSlice) so a result refetches the steps status.
        verifyBvnAddon: builder.mutation<BvnAddonResponse, { bvn: string; isSubjectConsent: boolean }>({
            query: (body) => ({
                url: `${urlConfig.kycUrl}/kyc/ng/bvn-addon`,
                method: 'POST',
                body,
            }),
            invalidatesTags: ['BankDepositAccount'],
        }),
        // Legacy (pre BVN add-on): the backend no longer captures a typed BVN.
        verifyBVN: builder.mutation<BVNVerificationResponse, BVNVerificationRequest>({
            query: (body) => ({
                url: 'bank-deposits/steps/verify-bvn',
                method: 'POST',
                body,
            }),
            invalidatesTags: ['BankDepositAccount'],
        }),
        verifyPhone: builder.mutation<PhoneVerificationResponse, PhoneVerificationRequest>({
            query: (body) => ({
                url: 'bank-deposits/steps/verify-phone',
                method: 'POST',
                body,
            }),
            invalidatesTags: ['BankDepositAccount'],
        }),
        createSubAccount: builder.mutation<CreateSubAccountResponse, void>({
            query: () => ({
                url: 'bank-deposits/steps/create-sub-account',
                method: 'POST',
            }),
            invalidatesTags: ['BankDepositAccount'],
        }),
        getMyDeposits: builder.query<DepositHistoryResponse, DepositHistoryParams>({
            query: ({ page, limit }) => `bank-deposits/my-deposits?page=${page}&limit=${limit}`,
            providesTags: ['BankDepositAccount'],
        }),
    }),
})

export const {
    useGetBankDepositAvailabilityQuery,
    useGetBankDepositAccountQuery,
    useGetBankDepositStepsStatusQuery,
    useVerifyBVNMutation,
    useVerifyBvnAddonMutation,
    useVerifyPhoneMutation,
    useCreateSubAccountMutation,
    useGetMyDepositsQuery,
} = bankDepositApi
